import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Monitor, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, setAuthToken } from "../../lib/api";
import { apiErr } from "../../lib/apiErr";

const fmt = (d) => (d ? new Date(d).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "—");

const sid = (s) => s.jti || s.id;

export default function MySessions({ onSignedOut }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState("");
  const [missing, setMissing] = useState(false);
  const load = useCallback(() => api.get("/auth/sessions").then((r) => { setItems(Array.isArray(r.data) ? r.data : r.data.items || []); setError(null); })
    .catch((e) => { if (e?.response?.status === 404) { setMissing(true); return; } setItems([]); setError(apiErr(e, "Gagal memuat sesi")); }), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (s) => {
    if (!window.confirm(s.current ? "Keluar dari sesi ini (perangkat yang sedang Anda pakai)?" : `Akhiri sesi di ${s.device || "perangkat ini"}?`)) return;
    setBusy(sid(s));
    try {
      const r = await api.delete(`/auth/sessions/${encodeURIComponent(sid(s))}`);
      if (r.data?.current) { toast.success("Sesi saat ini diakhiri"); onSignedOut(); return; }
      toast.success("Sesi diakhiri"); load();
    } catch (e) { toast.error(apiErr(e, "Gagal mengakhiri sesi")); } finally { setBusy(""); }
  };
  const others = (items || []).filter((s) => !s.current);
  const revokeOthers = async () => {
    if (!window.confirm(`Akhiri ${others.length} sesi lain? Perangkat ini tetap masuk.`)) return;
    setBusy("__all");
    try {
      const r = await api.post("/auth/logout-all");
      if (r.data?.access_token) setAuthToken(r.data.access_token);
      toast.success(`${others.length} sesi lain diakhiri`);
    } catch (e) { toast.error(apiErr(e, "Gagal mengakhiri sesi lain")); } finally { setBusy(""); load(); }
  };
  if (missing) return null;
  return (
    <div className="aivora-card p-6" data-testid="my-sessions">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold text-slate-900">Sesi login akun saya</h2>
          <p className="mt-1 text-xs text-slate-500">Perangkat yang sedang masuk dengan akun Anda. Akhiri sesi yang tidak Anda kenali.</p>
        </div>
        {others.length > 0 && (
          <button onClick={revokeOthers} disabled={!!busy} className="flex items-center gap-1.5 rounded-xl bg-red-600 px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50" data-testid="sessions-revoke-others">
            {busy === "__all" ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />} Keluarkan semua perangkat lain ({others.length})
          </button>
        )}
      </div>
      {error && <p className="mt-4 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="sessions-error">{error}</p>}
      {items && !items.length && !error && <p className="mt-4 rounded-xl bg-slate-50 px-4 py-6 text-center text-xs text-slate-500" data-testid="sessions-empty">Tidak ada sesi aktif.</p>}
      {!items ? <Loader2 size={14} className="mt-4 animate-spin text-slate-400" /> : items.length > 0 && (
        <ul className="mt-4 divide-y divide-slate-100 overflow-hidden rounded-2xl border border-line">
          {items.map((s) => (
            <li key={sid(s)} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`session-${sid(s)}`}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><Monitor size={16} /></span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">{s.device || "Perangkat tidak dikenal"}
                  {s.current && <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Sesi ini</span>}
                  {s.legacy && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">Lama</span>}</p>
                <p className="truncate text-[11px] text-slate-400">{s.ip || "—"}{s.location ? ` · ${s.location}` : ""} · masuk {fmt(s.created_at)} · terakhir aktif {fmt(s.last_seen_at)}</p>
              </div>
              <button onClick={() => revoke(s)} disabled={!!busy} className="flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 disabled:opacity-40" data-testid={`session-revoke-${sid(s)}`}>
                {busy === sid(s) ? <Loader2 size={13} className="animate-spin" /> : <LogOut size={13} />} {s.current ? "Keluar" : "Akhiri"}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
